import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ShoppingBag, ArrowRight, X, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Dialog animato mostrato quando il cliente aggiunge un prodotto al carrello.
 * Sostituisce il toast con un'esperienza più visuale e con CTA chiare.
 *
 * Props:
 *  - open (bool)
 *  - onClose ()
 *  - product { title, image, price/totalPrice, quantity }
 *  - cartCount (number) - articoli totali nel carrello dopo l'aggiunta
 */
export const AddedToCartDialog = ({ open, onClose, product, cartCount = 0 }) => {
  const navigate = useNavigate();

  // Chiusura con ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const price = Number(product?.totalPrice ?? product?.price ?? 0);
  const qty = Number(product?.quantity || 1);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            data-testid="added-to-cart-backdrop"
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 22, stiffness: 260 }}
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
            data-testid="added-to-cart-dialog"
          >
            {/* Close btn */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full hover:bg-slate-100 text-slate-500 flex items-center justify-center transition-colors"
              aria-label="Chiudi"
              data-testid="added-to-cart-close"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Header con animazione check */}
            <div className="bg-gradient-to-br from-teal-50 via-emerald-50 to-yellow-50 px-6 pt-8 pb-5 text-center relative overflow-hidden">
              {/* Sparkles decorativi */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                className="absolute top-4 left-6 text-yellow-400"
              >
                <Sparkles className="w-4 h-4" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.45, duration: 0.4 }}
                className="absolute top-8 right-8 text-teal-400"
              >
                <Sparkles className="w-3 h-3" />
              </motion.div>

              <motion.div
                initial={{ scale: 0, rotate: -30 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', damping: 12, stiffness: 200, delay: 0.05 }}
                className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white shadow-lg mb-2"
              >
                <CheckCircle2 className="w-8 h-8 text-emerald-500" strokeWidth={2.5} />
              </motion.div>
              <motion.h3
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="text-lg font-bold text-slate-800"
              >
                Prodotto aggiunto al carrello!
              </motion.h3>
            </div>

            {/* Product Preview */}
            <div className="px-6 py-4 border-b">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                  {product?.image ? (
                    <img
                      src={product.image}
                      alt={product.title || ''}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                      <ShoppingBag className="w-6 h-6" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 truncate" title={product?.title}>
                    {product?.title || 'Prodotto'}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-slate-500">Quantità:</span>
                    <span className="text-sm font-medium text-slate-700">{qty}</span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold text-[#008080]">
                    €{(price * qty).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            {/* Cart summary */}
            {cartCount > 0 && (
              <div className="px-6 py-2.5 bg-slate-50 border-b text-center">
                <span className="text-xs text-slate-600">
                  Il tuo carrello contiene ora{' '}
                  <strong className="text-slate-800">
                    {cartCount} {cartCount === 1 ? 'articolo' : 'articoli'}
                  </strong>
                </span>
              </div>
            )}

            {/* CTAs */}
            <div className="px-6 py-4 flex flex-col-reverse sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
                data-testid="added-to-cart-continue"
              >
                Continua a navigare
              </Button>
              <Button
                onClick={() => {
                  onClose?.();
                  navigate('/cart');
                }}
                className="flex-1 bg-[#008080] hover:bg-[#008080]/90 text-white group"
                data-testid="added-to-cart-goto-cart"
              >
                Vai al carrello
                <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AddedToCartDialog;
